import React, { useCallback, useEffect, useRef, useState } from "react";
import { MoreHorizontal, Pause, Pencil, Play, XCircle } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../../ui/dropdown-menu";
import StopWorkoutDialog from "../StopWorkoutDialog";
import useLocalStorage from "../../../hooks/useLocalStorage";
import { formatDuration } from "../../../utils/time";
import { cn } from "../../../lib/utils";

// ─── Stat formatters ─────────────────────────────────────────────────────────
const fmtInt = (v) => (v == null ? "—" : String(Math.round(v)));
const fmt1 = (v) => (v == null ? "—" : v.toFixed(1));
const fmtPace = (sec) => {
  if (sec == null) return "—";
  const m = Math.floor(sec / 60);
  const s = String(Math.round(sec % 60)).padStart(2, "0");
  return `${m}:${s}`;
};
const fmtBpm = ({ hr, hrBpm }) => {
  const v = hr?.getFreshSnapshot?.(3000)?.bpm ?? hrBpm;
  return fmtInt(v);
};
const fmtTime = ({ timing }) =>
  timing?.timeLeftInPhase != null ? formatDuration(timing.timeLeftInPhase) : "—";

// Each descriptor: how to label, unit, colour, and read the live value. `needs:
// "hr"` stats are filtered out of the cycle when no HR source is present.
const fmtKm = (m) => (m == null ? "—" : (m / 1000).toFixed(m < 10000 ? 2 : 1));
const fmtClock = (s) => (s == null ? "—" : formatDuration(s));

export const bikePrimaryStats = [
  { id: "power", label: "Power", unit: "W", color: "text-yellow-300", read: ({ m }) => fmtInt(m?.power_watts) },
  { id: "hr", label: "Heart rate", unit: "bpm", color: "text-red-400", needs: "hr", read: fmtBpm },
  { id: "time", label: "Time left", unit: "", color: "text-white", read: fmtTime },
  { id: "cadence", label: "Cadence", unit: "rpm", color: "text-white", read: ({ m }) => fmtInt(m?.cadence_rpm) },
  { id: "speed", label: "Speed", unit: "km/h", color: "text-white", read: ({ m }) => fmt1(m?.speed_kmh) },
  { id: "distance", label: "Distance", unit: "km", color: "text-white", read: ({ m }) => fmtKm(m?.total_distance_m) },
  { id: "kcal", label: "Calories", unit: "kcal", color: "text-white", read: ({ m }) => fmtInt(m?.total_energy_kcal) },
];

export const rowerPrimaryStats = [
  { id: "power", label: "Power", unit: "W", color: "text-yellow-300", read: ({ m }) => fmtInt(m?.power_watts) },
  { id: "hr", label: "Heart rate", unit: "bpm", color: "text-red-400", needs: "hr", read: fmtBpm },
  { id: "time", label: "Time left", unit: "", color: "text-white", read: fmtTime },
  { id: "spm", label: "Stroke rate", unit: "spm", color: "text-white", read: ({ m }) => fmtInt(m?.stroke_rate_spm) },
  { id: "pace", label: "Pace", unit: "/500m", color: "text-white", read: ({ m }) => fmtPace(m?.pace_sec_per_500m) },
  { id: "distance", label: "Distance", unit: "m", color: "text-white", read: ({ m }) => fmtInt(m?.total_distance_m) },
  { id: "kcal", label: "Calories", unit: "kcal", color: "text-white", read: ({ m }) => fmtInt(m?.total_energy_kcal) },
  { id: "strokes", label: "Strokes", unit: "", color: "text-white", read: ({ m }) => fmtInt(m?.stroke_count) },
];

// The smaller figures under Avg and Max: whatever else the machine sends.
// Each tile stays hidden until its machine reports it, so a bike with no
// resistance reading doesn't show a permanent dash.
export const bikeExtraStats = [
  { id: "distance", label: "Distance", unit: "km", read: (m) => m?.total_distance_m, format: fmtKm },
  { id: "kcal", label: "Calories", unit: "kcal", read: (m) => m?.total_energy_kcal, format: fmtInt },
  { id: "resistance", label: "Resistance", unit: "", read: (m) => m?.resistance_level, format: fmtInt },
  { id: "time", label: "On the bike", unit: "", read: (m) => m?.elapsed_time_s, format: fmtClock },
];

export const rowerExtraStats = [
  { id: "strokes", label: "Strokes", unit: "", read: (m) => m?.stroke_count, format: fmtInt },
  { id: "kcal", label: "Calories", unit: "kcal", read: (m) => m?.total_energy_kcal, format: fmtInt },
  { id: "avgPace", label: "Avg pace", unit: "/500m", read: (m) => m?.avg_pace_sec_per_500m, format: fmtPace },
  { id: "resistance", label: "Resistance", unit: "", read: (m) => m?.resistance_level, format: fmtInt },
];

/** The extra-figures tiles, ref-painted off the device like the rest. */
export const ExtraStats = ({ stats, subscribe, metricsRef, visibleRef }) => {
  const tileRefs = useRef({});
  const valueRefs = useRef({});
  useEffect(() => {
    const paint = () => {
      if (visibleRef && !visibleRef.current) return;
      const m = metricsRef?.current;
      for (const s of stats) {
        const v = s.read(m);
        const tile = tileRefs.current[s.id];
        const value = valueRefs.current[s.id];
        if (v == null || !tile || !value) continue;
        tile.style.display = "";
        value.textContent = s.format(v);
      }
    };
    paint();
    const unsubscribe = subscribe?.(() => paint());
    return () => unsubscribe?.();
  }, [stats, subscribe, metricsRef, visibleRef]);
  return (
    <div className="grid grid-cols-2 gap-3">
      {stats.map((s) => (
        <div
          key={s.id}
          // Hidden by style: the flex class would beat the hidden attribute.
          style={{ display: "none" }}
          ref={(node) => {
            tileRefs.current[s.id] = node;
          }}
          className="flex items-baseline justify-between gap-2 rounded-lg border border-white/5 bg-white/[0.03] px-3 py-2"
        >
          <span className="truncate text-xs uppercase tracking-widest text-white/40">{s.label}</span>
          <span className="text-lg font-mono font-bold tabular-nums">
            <span
              ref={(node) => {
                valueRefs.current[s.id] = node;
              }}
            >
              {"\u2014"}
            </span>
            {s.unit && <span className="ml-1 text-xs font-normal text-white/40">{s.unit}</span>}
          </span>
        </div>
      ))}
    </div>
  );
};

/**
 * The big, glanceable primary stat for the ERG dashboards. Concept2/Garmin
 * style: tap the number (or a pill) to cycle which metric is featured; the
 * choice persists per modality. Painted straight into the DOM via refs so the
 * 10 Hz device stream never triggers a React render — the same trick the rest
 * of the dashboard uses. Time updates ride the dashboard's 1 Hz re-render.
 */
export const PrimaryStat = ({
  stats,
  storageKey,
  subscribe,
  metricsRef,
  hr,
  hrBpm,
  timing,
  visibleRef,
}) => {
  const [statId, setStatId] = useLocalStorage(storageKey, "power");
  const hasHr = !!hr || hrBpm != null;
  const available = stats.filter((s) => s.needs !== "hr" || hasHr);
  const stat = available.find((s) => s.id === statId) || available[0];

  // Refs let the stable `paint` callback always read the latest selection and
  // data sources without resubscribing on every render.
  const statRef = useRef(stat);
  statRef.current = stat;
  const ctxRef = useRef({ hr, hrBpm, timing });
  ctxRef.current = { hr, hrBpm, timing };
  const valueRef = useRef(null);

  const paint = useCallback(() => {
    if (visibleRef && !visibleRef.current) return;
    const s = statRef.current;
    const node = valueRef.current;
    if (!s || !node) return;
    node.textContent = s.read({ m: metricsRef?.current, ...ctxRef.current });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [metricsRef]);

  // Device-stream driven repaint (power/cadence/spm/pace/distance @ ~10 Hz).
  useEffect(() => {
    paint();
    const unsubscribe = subscribe?.(() => paint());
    return () => unsubscribe?.();
  }, [subscribe, paint]);

  // Repaint on every render — keeps the Time stat (and HR fallback) fresh at
  // the dashboard's 1 Hz tick, and reflects a selection change immediately.
  useEffect(() => {
    paint();
  });

  const cycle = () => {
    const i = available.findIndex((s) => s.id === stat.id);
    setStatId(available[(i + 1) % available.length].id);
  };

  return (
    <div className="flex flex-col items-center justify-center mt-2 select-none">
      <button
        type="button"
        onClick={cycle}
        aria-label={`Primary stat: ${stat.label}. Tap to change.`}
        className="flex items-baseline gap-3 rounded-lg px-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <span
          ref={valueRef}
          className={cn(
            "text-[7rem] leading-none font-mono font-bold tabular-nums",
            stat.color,
          )}
        >
          —
        </span>
        {stat.unit && (
          <span className="text-2xl text-white/60 self-end mb-2">
            {stat.unit}
          </span>
        )}
      </button>
      <span className="mt-1 text-xs uppercase tracking-widest text-white/40">
        {stat.label}
      </span>
      {available.length > 1 && (
        <div className="mt-2 flex flex-wrap items-center justify-center gap-1">
          {available.map((s) => (
            <button
              key={s.id}
              type="button"
              onClick={() => setStatId(s.id)}
              aria-pressed={s.id === stat.id}
              className={cn(
                "px-2.5 py-0.5 rounded-full text-xs font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                s.id === stat.id
                  ? "bg-white/85 text-black"
                  : "bg-white/10 text-white/60 hover:text-white",
              )}
            >
              {s.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

/**
 * The pause button, in the timing strip at the top of the ERG dashboards so
 * it never sits on a figure. Running only: paused, the bar below takes over.
 */
export const PauseButton = ({ isRunning, onPlayPause }) => (
  <button
    type="button"
    onClick={onPlayPause}
    aria-label={isRunning ? "Pause workout" : "Resume workout"}
    className="flex h-11 w-11 items-center justify-center rounded-full border border-white/20 bg-white/10 text-white transition-colors hover:bg-white/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
  >
    {isRunning ? <Pause className="h-5 w-5" /> : <Play className="h-5 w-5 fill-current ml-0.5" />}
  </button>
);

/**
 * Pause/resume transport for the ERG dashboards. PauseButton, in the timing
 * strip, pauses the engine (same `onPlayPause` the Coach ring uses). Paused, the dashboard dims
 * and a bar above the Coach/Rower switcher says where you are, with Resume
 * and a menu of what the Coach's pause screen offers (#197): its own actions
 * (Go live), Edit session, and Stop workout with the same save/discard
 * dialog. Renders nothing in standalone (no-engine) use.
 *
 * `pauseMenu`: { stepName?, actions?: [{ key, icon, label, onClick }],
 * onEditSession?, stopActions?, collectStopFeedback? }.
 */
export const DashboardTransport = ({ isPaused, isRunning, onPlayPause, timing, pauseMenu }) => {
  const [stopOpen, setStopOpen] = useState(false);
  if (!onPlayPause) return null;
  const actions = (pauseMenu?.actions || []).filter((a) => a.onClick);
  const stopActions = pauseMenu?.stopActions || [];
  const hasMenu = actions.length > 0 || !!pauseMenu?.onEditSession || stopActions.length > 0;
  const step = pauseMenu?.stepName || timing?.phaseLabel;
  const where = step
    ? timing?.timeLeftInPhase != null
      ? `${step} · ${formatDuration(timing.timeLeftInPhase)} left`
      : step
    : null;
  // A dialog opened straight from a menu item fights the menu for focus and
  // the pointer lock, so it opens once the menu has closed.
  const later = (fn) => () => setTimeout(fn, 0);

  // Running, the pause button lives in the timing strip (PauseButton).
  if (!isPaused) return null;

  return (
    <>
      <div className="pointer-events-none absolute inset-0 z-10 bg-black/30" aria-hidden="true" />
      <div className="absolute inset-x-3 bottom-14 z-20 flex items-center gap-2 rounded-2xl border border-border bg-card py-2 pl-3.5 pr-2 shadow-lg">
        <div className="min-w-0 flex-1 text-left leading-tight">
          <p className="text-sm font-semibold text-foreground">Paused</p>
          {where && <p className="truncate text-xs text-muted-foreground tabular-nums">{where}</p>}
        </div>
        <button
          type="button"
          onClick={onPlayPause}
          className="flex shrink-0 items-center gap-1.5 rounded-full bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary/90 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <Play className="h-4 w-4 fill-current" />
          Resume
        </button>
        {hasMenu && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                aria-label="More"
                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-border text-foreground transition-colors hover:bg-muted/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <MoreHorizontal className="h-4 w-4" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" side="top" className="w-48">
              {actions.map(({ key, icon: Icon, label, onClick }) => (
                <DropdownMenuItem key={key || label} onSelect={later(onClick)}>
                  {Icon && <Icon className="mr-2 h-4 w-4" />}
                  {label}
                </DropdownMenuItem>
              ))}
              {pauseMenu?.onEditSession && (
                <DropdownMenuItem onSelect={later(pauseMenu.onEditSession)}>
                  <Pencil className="mr-2 h-4 w-4" />
                  Edit session
                </DropdownMenuItem>
              )}
              {stopActions.length > 0 && (
                <>
                  {(actions.length > 0 || pauseMenu?.onEditSession) && <DropdownMenuSeparator />}
                  <DropdownMenuItem
                    onSelect={later(() => setStopOpen(true))}
                    className="text-destructive focus:text-destructive"
                  >
                    <XCircle className="mr-2 h-4 w-4" />
                    Stop workout
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
      {stopActions.length > 0 && (
        <StopWorkoutDialog
          stopActions={stopActions}
          collectStopFeedback={pauseMenu?.collectStopFeedback}
          open={stopOpen}
          onOpenChange={setStopOpen}
        />
      )}
    </>
  );
};
